"use client";

import { AlertTriangle, CalendarX, Check, Copy, Moon, Sun } from "lucide-react";
import Link from "next/link";
import { useEffect, useState, type ReactNode } from "react";

import {
	OffPeakNotice,
	isOffPeak,
	type Schedule,
} from "@/components/off-peak-notice";
import { ProviderLogo } from "@/core/brand-logo";
import { discountFraction } from "@/core/discount";
import {
	RETIREMENT_NOTICE_DAYS,
	getDefaultProviderMapping,
	isMappingDeactivated,
	shouldShowDeactivationNotice,
} from "@/core/model-status";
import { formatNumber } from "@/core/numbers";
import { Tag, cx } from "@/ds";

type Price = string | null | undefined;

/** The parts of a provider mapping this card reads. */
export interface Offer {
	id: string;
	providerId: string;
	region?: string | null;
	discount?: string | null;
	stability?: string | null;
	deactivatedAt?: Date | string | null;
	contextSize?: number | null;
	maxOutput?: number | null;
	inputPrice?: Price;
	outputPrice?: Price;
	cachedInputPrice?: Price;
	cacheWriteInputPrice?: Price;
	cacheWriteInputPrice1h?: Price;
	inputAudioPrice?: Price;
	outputAudioPrice?: Price;
	inputAudioHourPrice?: Price;
	inputCharacterPrice?: Price;
	imageInputPrice?: Price;
	imageOutputPrice?: Price;
	imageOutputTokensByResolution?: Record<string, number> | null;
	perImagePrice?: Record<string, string | number> | null;
	perSecondPrice?: Record<string, string | number> | null;
	requestPrice?: Price;
	webSearchPrice?: Price;
	offPeakSchedule?: Schedule | null;
	peakPricing?: {
		peak: { inputPrice: string; outputPrice: string; cachedInputPrice?: Price };
		offPeak: {
			inputPrice: string;
			outputPrice: string;
			cachedInputPrice?: Price;
		};
	} | null;
	pricingTiers?:
		| {
				name: string;
				upToTokens?: number | null;
				inputPrice: string;
				outputPrice: string;
		  }[]
		| null;
	serviceTiers?: string[] | null;
	reasoningEfforts?: string[] | null;
	streaming?: boolean | null;
	vision?: boolean | null;
	tools?: boolean | null;
	reasoning?: boolean | null;
	jsonOutput?: boolean | null;
	jsonOutputSchema?: boolean | null;
	webSearch?: boolean | null;
	audio?: boolean | null;
	document?: boolean | null;
	realtime?: boolean | null;
}

export interface OfferProvider {
	id: string;
	name?: string | null;
	modelCardBadge?: string | null;
	serviceTiers?:
		| { id: string; name: string; multiplier: number; description?: string }[]
		| null;
}

const FEATURES: [keyof Offer, string][] = [
	["streaming", "Streaming"],
	["tools", "Tools"],
	["vision", "Vision"],
	["reasoning", "Reasoning"],
	["jsonOutput", "JSON mode"],
	["jsonOutputSchema", "Structured output"],
	["webSearch", "Web search"],
	["audio", "Audio"],
	["document", "Documents"],
	["realtime", "Realtime"],
];

function num(value: Price | number): number | null {
	if (value === null || value === undefined || value === "") {
		return null;
	}
	const parsed = Number(value);
	return Number.isFinite(parsed) ? parsed : null;
}

function money(value: number, places = 4) {
	return `$${Number(value.toFixed(places))}`;
}

/** One price, struck through and discounted when a discount applies. */
function Amount({
	value,
	scale,
	off,
	places,
}: {
	value: Price | number;
	scale: number;
	off: number;
	places?: number;
}) {
	const base = num(value);
	if (base === null) {
		return <span className="text-muted-foreground">n/a</span>;
	}
	const full = base * scale;
	if (off <= 0) {
		return <span>{money(full, places)}</span>;
	}
	return (
		<span>
			<s className="mr-1.5 text-xs text-muted-foreground">
				{money(full, places)}
			</s>
			<span className="font-semibold text-emerald-600 dark:text-emerald-400">
				{money(full * (1 - off), places)}
			</span>
		</span>
	);
}

function Line({ label, children }: { label: string; children: ReactNode }) {
	return (
		<div className="flex items-baseline justify-between gap-3 py-1.5 text-sm">
			<dt className="text-muted-foreground">{label}</dt>
			<dd className="text-right font-mono tabular-nums">{children}</dd>
		</div>
	);
}

function CopyId({ value }: { value: string }) {
	const [done, setDone] = useState(false);
	return (
		<button
			type="button"
			onClick={() => {
				void navigator.clipboard.writeText(value);
				setDone(true);
				setTimeout(() => setDone(false), 1500);
			}}
			title="Copy model ID"
			className="flex w-full items-center gap-2 rounded-lg border border-border bg-muted/40 px-3 py-2 text-left font-mono text-xs hover:border-primary/40"
		>
			<span className="min-w-0 flex-1 truncate">{value}</span>
			{done ? (
				<Check className="size-3.5 text-emerald-500" />
			) : (
				<Copy className="size-3.5 text-muted-foreground" />
			)}
		</button>
	);
}

/**
 * One provider's offer for a model: prices (with discounts, regions, service
 * tiers and peak hours), limits, features and retirement notices.
 */
export function OfferCard({
	modelId,
	provider,
	offers,
	providerHref,
	headerExtra,
}: {
	modelId: string;
	provider: OfferProvider;
	/** This provider's mappings for the model, one per region. */
	offers: Offer[];
	providerHref?: string;
	headerExtra?: ReactNode;
}) {
	const [region, setRegion] = useState<string | null | undefined>(undefined);
	const [hours, setHours] = useState<"peak" | "offPeak">("peak");
	const [tierId, setTierId] = useState("standard");

	const offer =
		(region !== undefined
			? offers.find((item) => (item.region ?? null) === region)
			: undefined) ?? getDefaultProviderMapping(offers);
	const off = discountFraction(offer.discount);
	const tiers = (provider.serviceTiers ?? []).filter((tier) =>
		(offer.serviceTiers ?? []).includes(tier.id),
	);
	const multiplier = tiers.find((tier) => tier.id === tierId)?.multiplier ?? 1;
	const timed = offer.peakPricing?.[hours];
	// Open on the prices that apply right now.
	useEffect(() => {
		if (offer.offPeakSchedule && isOffPeak(offer.offPeakSchedule, new Date())) {
			setHours("offPeak");
		}
	}, [offer.offPeakSchedule]);
	const offPeakShare = offer.peakPricing
		? Number(offer.peakPricing.offPeak.inputPrice) /
			Number(offer.peakPricing.peak.inputPrice || 1)
		: null;
	const offPeakDiscount = offPeakShare === null ? null : 1 - offPeakShare;
	const token = (value: Price) => (num(value) === null ? null : value);
	const tokenRows: [string, Price][] = [
		["Input", timed?.inputPrice ?? offer.inputPrice],
		["Cached input", timed?.cachedInputPrice ?? offer.cachedInputPrice],
		["Cache write (5 min)", offer.cacheWriteInputPrice],
		["Cache write (1 hour)", offer.cacheWriteInputPrice1h],
		["Output", timed?.outputPrice ?? offer.outputPrice],
		["Audio input", offer.inputAudioPrice],
		["Audio output", offer.outputAudioPrice],
		["Image input", offer.imageInputPrice],
	];
	const shownTokens = tokenRows.filter(([, value]) => token(value) !== null);

	const imageOut = (() => {
		const per = num(offer.imageOutputPrice);
		const sizes = offer.imageOutputTokensByResolution;
		if (per === null || !sizes) {
			return null;
		}
		const [size, tokens] =
			Object.entries(sizes).find(([key]) => key !== "default") ??
			Object.entries(sizes)[0] ??
			[];
		return size && tokens ? { size, cost: per * tokens } : null;
	})();

	const retired = isMappingDeactivated(offer);
	const retiring = !retired && shouldShowDeactivationNotice(offer);
	const shaky =
		offer.stability === "unstable" || offer.stability === "experimental";
	const regions = offers.filter((item) => item.region);
	const features = FEATURES.filter(([key]) => offer[key] === true);
	const name = provider.name ?? provider.id;

	return (
		<article
			className={cx(
				"flex h-full flex-col rounded-xl border border-border bg-background p-4",
				retired && "opacity-70",
			)}
		>
			<header className="flex items-start gap-3">
				<ProviderLogo provider={provider.id} className="size-9 shrink-0" />
				<div className="min-w-0 flex-1">
					<div className="flex flex-wrap items-center gap-1.5">
						{providerHref ? (
							<Link
								href={providerHref}
								className="font-semibold hover:underline"
							>
								{name}
							</Link>
						) : (
							<span className="font-semibold">{name}</span>
						)}
						{provider.modelCardBadge ? (
							<Tag tone="brand">{provider.modelCardBadge}</Tag>
						) : null}
						{off > 0 ? (
							<Tag tone="good">{Math.round(off * 100)}% off</Tag>
						) : null}
						{shaky ? <Tag tone="warn">{offer.stability}</Tag> : null}
					</div>
					{headerExtra}
				</div>
			</header>

			{retired || retiring ? (
				<p
					className={cx(
						"mt-3 flex items-center gap-2 rounded-lg px-3 py-2 text-xs",
						retired
							? "bg-rose-500/10 text-rose-700 dark:text-rose-300"
							: "bg-amber-500/10 text-amber-800 dark:text-amber-200",
					)}
				>
					<CalendarX className="size-3.5 shrink-0" />
					{retired ? "Retired on " : "Retires on "}
					{new Date(offer.deactivatedAt as string).toLocaleDateString()}
					{retiring ? ` (within ${RETIREMENT_NOTICE_DAYS} days)` : ""}
				</p>
			) : null}
			{shaky ? (
				<p className="mt-3 flex items-center gap-2 text-xs text-amber-700 dark:text-amber-300">
					<AlertTriangle className="size-3.5 shrink-0" />
					This provider marks the model {offer.stability}; expect changes.
				</p>
			) : null}

			{regions.length > 0 ? (
				<div
					className="mt-3 flex flex-wrap gap-1"
					role="group"
					aria-label="Region"
				>
					{offers.map((item) => {
						const value = item.region ?? null;
						const active = (offer.region ?? null) === value;
						return (
							<button
								key={item.id}
								type="button"
								aria-pressed={active}
								onClick={() => setRegion(value)}
								className={cx(
									"rounded-full border px-2.5 py-0.5 text-xs",
									active
										? "border-primary bg-primary/10 text-primary"
										: "border-border text-muted-foreground hover:text-foreground",
								)}
							>
								{value ?? "Any region"}
							</button>
						);
					})}
				</div>
			) : null}

			{tiers.length > 0 || offer.peakPricing ? (
				<div className="mt-3 flex flex-wrap items-center gap-2 text-xs">
					{tiers.length > 0 ? (
						<select
							aria-label="Service tier"
							value={tierId}
							onChange={(event) => setTierId(event.target.value)}
							className="rounded-md border border-border bg-background px-2 py-1"
						>
							<option value="standard">Standard (1×)</option>
							{tiers.map((tier) => (
								<option key={tier.id} value={tier.id} title={tier.description}>
									{tier.name} ({tier.multiplier}×)
								</option>
							))}
						</select>
					) : null}
					{offer.peakPricing ? (
						<div className="flex rounded-md border border-border p-0.5">
							{(
								[
									["peak", "Peak", Sun],
									["offPeak", "Off-peak", Moon],
								] as const
							).map(([key, label, Icon]) => (
								<button
									key={key}
									type="button"
									aria-pressed={hours === key}
									onClick={() => setHours(key)}
									className={cx(
										"flex items-center gap-1 rounded px-2 py-0.5",
										hours === key
											? "bg-muted font-medium"
											: "text-muted-foreground",
									)}
								>
									<Icon className="size-3" />
									{label}
								</button>
							))}
						</div>
					) : null}
				</div>
			) : null}

			{offer.offPeakSchedule ? (
				<div className="mt-3">
					<OffPeakNotice
						schedule={offer.offPeakSchedule}
						discount={offPeakDiscount}
					/>
				</div>
			) : null}

			<dl className="mt-3 divide-y divide-border/60">
				{shownTokens.length > 0 ? (
					<p className="pb-1 text-[11px] uppercase tracking-wide text-muted-foreground">
						Per 1M tokens
					</p>
				) : null}
				{shownTokens.map(([label, value]) => (
					<Line key={label} label={label}>
						<Amount value={value} scale={1e6 * multiplier} off={off} />
					</Line>
				))}
				{offer.perImagePrice
					? Object.entries(offer.perImagePrice).map(([size, value]) => (
							<Line
								key={`image-${size}`}
								label={size === "default" ? "Per image" : `Per image, ${size}`}
							>
								<Amount value={value} scale={multiplier} off={off} places={5} />
							</Line>
						))
					: null}
				{imageOut ? (
					<Line
						label={`Per image, ${imageOut.size === "default" ? "est." : imageOut.size}`}
					>
						<Amount value={imageOut.cost} scale={multiplier} off={off} />
					</Line>
				) : null}
				{offer.perSecondPrice
					? Object.entries(offer.perSecondPrice).map(([kind, value]) => (
							<Line
								key={`second-${kind}`}
								label={`Per second${kind === "default" ? "" : `, ${kind.replace(/_/g, " ")}`}`}
							>
								<Amount value={value} scale={multiplier} off={off} places={5} />
							</Line>
						))
					: null}
				{num(offer.inputAudioHourPrice) ? (
					<Line label="Audio input, per hour">
						<Amount
							value={offer.inputAudioHourPrice}
							scale={multiplier}
							off={off}
						/>
					</Line>
				) : null}
				{num(offer.inputCharacterPrice) ? (
					<Line label="Per 1K characters">
						<Amount
							value={offer.inputCharacterPrice}
							scale={1000 * multiplier}
							off={off}
						/>
					</Line>
				) : null}
				{num(offer.requestPrice) ? (
					<Line label="Per request">
						<Amount
							value={offer.requestPrice}
							scale={multiplier}
							off={off}
							places={3}
						/>
					</Line>
				) : null}
				{num(offer.webSearchPrice) ? (
					<Line label="Per web search">
						<Amount
							value={offer.webSearchPrice}
							scale={multiplier}
							off={off}
							places={3}
						/>
					</Line>
				) : null}
			</dl>

			{offer.pricingTiers?.length ? (
				<details className="mt-2 text-xs">
					<summary className="cursor-pointer text-muted-foreground">
						Prices by prompt length
					</summary>
					<table className="mt-2 w-full tabular-nums">
						<tbody>
							{offer.pricingTiers.map((tier) => (
								<tr key={tier.name}>
									<td className="py-0.5 text-muted-foreground">
										{tier.upToTokens
											? `Up to ${formatNumber(tier.upToTokens)} tokens`
											: tier.name}
									</td>
									<td className="text-right font-mono">
										<Amount
											value={tier.inputPrice}
											scale={1e6 * multiplier}
											off={off}
										/>
										{" / "}
										<Amount
											value={tier.outputPrice}
											scale={1e6 * multiplier}
											off={off}
										/>
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</details>
			) : null}

			<div className="mt-3 grid grid-cols-2 gap-2 text-xs">
				<div className="rounded-lg bg-muted/50 px-3 py-2">
					<p className="text-muted-foreground">Context</p>
					<p className="font-semibold tabular-nums">
						{offer.contextSize ? formatNumber(offer.contextSize) : "n/a"}
					</p>
				</div>
				<div className="rounded-lg bg-muted/50 px-3 py-2">
					<p className="text-muted-foreground">Max output</p>
					<p className="font-semibold tabular-nums">
						{offer.maxOutput ? formatNumber(offer.maxOutput) : "n/a"}
					</p>
				</div>
			</div>

			{features.length > 0 || offer.reasoningEfforts?.length ? (
				<div className="mt-3 flex flex-wrap gap-1">
					{features.map(([key, label]) => (
						<span
							key={key}
							className="rounded-full bg-sky-500/10 px-2 py-0.5 text-[11px] text-sky-700 dark:text-sky-300"
						>
							{label}
						</span>
					))}
					{offer.reasoningEfforts?.length ? (
						<span className="rounded-full bg-violet-500/10 px-2 py-0.5 text-[11px] text-violet-700 dark:text-violet-300">
							Effort: {offer.reasoningEfforts.join(", ")}
						</span>
					) : null}
				</div>
			) : null}

			<div className="mt-auto pt-4">
				<CopyId value={`${provider.id}/${modelId}`} />
			</div>
		</article>
	);
}
